import Loading from "@/components/Loading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ProviderProfilePage } from "./ProviderProfilePage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, ShieldAlert } from "lucide-react";
import { useTRPC } from "@/trpc";
import { useProviderProfileData } from "@/features/profile/hooks/useProviderProfileData";
import { RevealProviderDialog } from "@/features/provider/reveal-provider-dialog";
import { useState } from "react";
import { toast } from "sonner";

function getHiddenProviderBannerText({
  isAdmin,
  canReveal,
}: {
  isAdmin: boolean;
  canReveal: boolean;
}): string {
  if (isAdmin) {
    return "This provider is still hidden from the organization. As an admin, you can view their identity and documents, or reveal them to the team.";
  }
  if (canReveal) {
    return "Provider identity is hidden. Name, NPI, and documents are not visible.";
  }
  return "Provider identity is hidden. Name, NPI, and documents are not visible. Ask a business unit admin to reveal this provider.";
}

export const ProfileModal = ({
  userId,
  onClose,
  organizationBusinessUnitId: organizationBusinessUnitIdProp,
  showChatButton,
}: {
  userId: string;
  onClose: () => void;
  organizationBusinessUnitId?: string;
  showChatButton?: boolean;
}) => {
  const organizationBusinessUnitId =
    organizationBusinessUnitIdProp || undefined;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);

  const providerUserQuery = useQuery(
    trpc.profile.getSharedUser.queryOptions({
      userId,
      organizationBusinessUnitId,
    }),
  );
  const profileData = useProviderProfileData(
    userId,
    organizationBusinessUnitId,
  );
  const currentUserQuery = useQuery(trpc.user.getUser.queryOptions());
  const credentialEditAccessQuery = useQuery(
    trpc.credentialFiles.getCredentialEditAccess.queryOptions({
      userId,
      organizationBusinessUnitId,
    }),
  );
  const requestCredentialEditAccessMutation = useMutation(
    trpc.credentialFiles.requestCredentialEditAccess.mutationOptions({
      onSuccess: async () => {
        if (!organizationBusinessUnitId) {
          return;
        }

        await queryClient.invalidateQueries(
          trpc.credentialFiles.getCredentialEditAccess.queryOptions({
            userId,
            organizationBusinessUnitId,
          }),
        );
        toast.success("Credential edit request sent");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to send request");
      },
    }),
  );

  const isLoading =
    providerUserQuery.isLoading ||
    currentUserQuery.isLoading ||
    credentialEditAccessQuery.isLoading;
  const providerUser = providerUserQuery.data;
  const isAdmin = currentUserQuery.data?.is_admin ?? false;
  const isHidden = providerUser?.isRevealed === false;
  const canReveal = providerUser?.canReveal ?? false;
  const shouldObfuscateProvider = isHidden && !isAdmin;

  const hasError =
    providerUserQuery.error ||
    currentUserQuery.error ||
    credentialEditAccessQuery.error ||
    !providerUser;
  const allowDocumentUploads = organizationBusinessUnitId
    ? Boolean(
        credentialEditAccessQuery.data?.canEdit &&
          credentialEditAccessQuery.data.request?.status === "approved",
      )
    : isAdmin;

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[95vw] max-h-[90vh] w-[95dvw] overflow-y-auto p-0 md:p-6">
          <DialogHeader className="p-4 md:p-0">
            <DialogTitle className="sr-only">Provider Profile</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <Loading className="h-20 w-20" />
              </div>
            ) : hasError ? (
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900">Error</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    The provider profile couldn't be loaded.
                  </p>
                </div>
              </div>
            ) : (
              (() => {
                if (profileData.isLoading) {
                  return (
                    <div className="flex items-center justify-center p-12">
                      <Loading className="h-20 w-20" />
                    </div>
                  );
                }
                if (profileData.error || !profileData.data) {
                  return (
                    <div className="flex items-center justify-center p-12">
                      <div className="text-center">
                        <h3 className="text-lg font-medium text-gray-900">
                          Error
                        </h3>
                        <p className="mt-2 text-sm text-gray-500">
                          The provider profile couldn't be loaded.
                        </p>
                      </div>
                    </div>
                  );
                }
                return (
                  <>
                    {isHidden && organizationBusinessUnitId && (
                      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mx-4 mt-2 mb-0 md:mx-0">
                        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" />
                        <p className="text-sm text-amber-800 flex-1">
                          {getHiddenProviderBannerText({ isAdmin, canReveal })}
                        </p>
                        {canReveal && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 border-amber-200 hover:bg-amber-100 hover:text-amber-700 shrink-0 flex items-center gap-1"
                            onClick={() => setRevealDialogOpen(true)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Reveal
                          </Button>
                        )}
                      </div>
                    )}
                    <ProviderProfilePage
                      user={providerUser}
                      credentials={profileData.data.credentials}
                      jobPreferences={profileData.data.jobPreferences}
                      sharePermissions={profileData.data.sharePermissions}
                      forceMobileView={true}
                      allowDocumentUploads={allowDocumentUploads}
                      credentialEditRequest={
                        credentialEditAccessQuery.data?.request ?? null
                      }
                      isRequestingCredentialEditAccess={
                        requestCredentialEditAccessMutation.isPending
                      }
                      onRequestCredentialEditAccess={
                        organizationBusinessUnitId
                          ? (message) =>
                              requestCredentialEditAccessMutation.mutate({
                                providerUserId: userId,
                                organizationBusinessUnitId,
                                message,
                              })
                          : undefined
                      }
                      organizationBusinessUnitId={organizationBusinessUnitId}
                      isProviderHidden={shouldObfuscateProvider}
                      showChatButton={showChatButton}
                    />
                  </>
                );
              })()
            )}
          </div>
        </DialogContent>
      </Dialog>

      {organizationBusinessUnitId && (
        <RevealProviderDialog
          open={revealDialogOpen}
          onOpenChange={setRevealDialogOpen}
          providerUserId={userId}
          organizationBusinessUnitId={organizationBusinessUnitId}
        />
      )}
    </>
  );
};
